import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

export type PreventScrollOptions = {
  /** Whether the scroll lock is disabled. */
  isDisabled?: boolean;

  /**
   * Reference to the outermost element.
   * This is used to avoid scrollbars displacing content. */
  outerElementRef?: RefObject<HTMLElement>;

  /**
   * Custom style function to apply to the document body.
   * This is used to overwrite what styles the hook would normally apply.
   *
   * Should return a function that restores the original styles.
   */
  customStyleFunction?: PreventScrollCustomStyleFunction;
};

export type PreventScrollCustomStyleFunction = (
  scrollPaddingRightVariable: string,
  scrollPaddingRightValue: string | undefined
) => () => void;

const scrollPaddingRight = '--scrollPaddingRight';

/**
 * Prevents scrolling on the document body on mount, and
 * restores it on unmount. Works great on mobile Safari.
 * Avoids content shifting due to scrollbars disappearing.
 */
export function usePreventScroll({
  isDisabled,
  outerElementRef,
  customStyleFunction,
}: PreventScrollOptions = {}) {
  const fnRef = useRef<PreventScrollCustomStyleFunction | undefined>(undefined);
  fnRef.current = customStyleFunction;

  useEffect(() => {
    if (isDisabled) {
      return;
    }

    const scrollingElement = document.scrollingElement;
    const offset = scrollingElement?.scrollTop ?? 0;
    const bodyScrollbarVisible =
      (scrollingElement?.scrollHeight ?? 0) >
      (scrollingElement?.clientHeight ?? 0);

    const outerElement = outerElementRef?.current ?? null;

    let scrollPaddingRightValue: string | undefined = undefined;

    for (const el of [outerElement, document.body]) {
      if (el) {
        const current = window.getComputedStyle(el).paddingRight;
        const extra = `${
          window.innerWidth - document.documentElement.clientWidth
        }px`;
        const scrollbarVisible = el.scrollHeight > el.clientHeight;

        if (
          bodyScrollbarVisible &&
          ((el === outerElement && !scrollbarVisible) || el === document.body)
        ) {
          const value =
            current && current !== '0px'
              ? `calc(${current} + ${extra})`
              : extra;

          if (el === outerElement) {
            el.style.setProperty(scrollPaddingRight, value);
          } else {
            el.style.paddingRight = value;
            scrollPaddingRightValue = extra;
          }
        }
      }
    }

    const style = document.body.style;

    let cleanup: (() => void) | undefined = undefined;

    if (fnRef.current) {
      cleanup = fnRef.current(scrollPaddingRight, scrollPaddingRightValue);
    } else {
      style.top = offset * -1 + 'px';
      style.position = 'fixed';
      style.overflow = 'hidden';
      style.left = '0';
      style.right = '0';
    }

    return () => {
      if (outerElement) {
        outerElement.style.removeProperty(scrollPaddingRight);
      }

      style.paddingRight = '';

      if (cleanup) {
        cleanup();
      } else {
        style.top = '';
        style.position = '';
        style.left = '';
        style.right = '';
        style.overflow = '';

        if (scrollingElement) {
          scrollingElement.scrollTop = offset;
        }
      }
    };
  }, [isDisabled, outerElementRef]);
}
